import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {
    DEFAULT_LEFT,
    SEARCH_MODEL
} from "../../../../../../../components/table/Model/TableModel";
import CustomTh from "../../../../../../../components/table/CustomTh";
import CustomTd from "../../../../../../../components/table/CustomTd";
import CustomImageTable from "../../../../../../../components/CustomImageTable";
import {
    CheckIconsOptions,
    DetailsIconsOptions, EditUnderlinedIcon,
    IconAvatarTable, IconClockTable, IconCloseChip, IconScheduleTable
} from "../../../../../../../components/buttons/IconButtons";
import CustomChip from "../../../../../../../components/CustomChip";
import {status, STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../../../../enums/statusColorEnum";
import DropDownMenuProfile from "../../../../../../../components/DropDown/DropDownMenuProfile";
import DropDownMenuItem from "../../../../../../../components/DropDown/DropDownMenuItem";
import {SimpleSearch} from "../../../../../../../reduxToolkit/Search/SearchApi";
import TableFunction from "../../../../../../../components/table/TableFunction";
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    getDateFormat,
    getTimeFormat
} from "../../../../../../../Apis/functions";
import routes from "../../../../../../../Routes/routes";
import {ATTENDANCE_SUBVIEW_MODEL, TABS_ATTENDANCE} from "../../../Model/Model";
import {useNavigate} from "react-router-dom";
import {permissionObj} from "../../../../../../../Helpers/permission";
import {handleSubmissionDetails} from "../../History/Model/Model";
import {GetAttendancePeriodRequest} from "../../../../../../../reduxToolkit/AttendanceAdjustment/AttendanceAdjustmentApi";

/**
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : FullStack
 * @Email : kpozos@ibl.mx
 *
 */
const AttendanceRequestedTableApp = ({
    selectedList,
    setSelectedList,
    searchElement,
    modified,
    setModified,
    setBodyForExport,
    setIsAllChecked,
    option
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const {getAttendancePeriodRequest} = useSelector((state) => state.AttendanceAdjustmentSlice);
    const { getUserByIdAtEntry} = useSelector((state) => state.authenticatioauthennSlice);
    const tableId = "tbl_art_attendance_request"; // Set the table ID for identification
    const [results, setResults] = useState(null); // Store the search results
    const searchId = 'txt_art_attendance_request';
    const leftPosition = DEFAULT_LEFT;
    const [showApprove, setShowApprove] = useState(false);
    const [showDenied, setShowDenied] = useState(false);
    const [row, setRow] = useState(null);
    const isAdmin = DEV || permission?.includes(permissionObj?.WEB_ATTENDANCE_SUBMISSION_PERIOD_REVIEW_READ) ||
        permission?.includes(permissionObj?.WEB_ATTENDANCE_SUBMISSION_REQUEST_READ);

    // Generic modal configuration for approve/deny/request changes actions
    const genericCommentModals = [
        {
            show: showApprove,
            setter: setShowApprove,
            title: 'approve_request',
            setRow: setRow,
            row: row,
            status: STATUS_MODEL.APPROVED,
            requiredComment: false
        },
        {
            show: showDenied,
            setter: setShowDenied,
            title: 'decline_comment',
            setRow: setRow,
            row: row,
            status: STATUS_MODEL.DENIED,
            requiredComment: true
        }
    ];

    /**
     * Loads attendance period request data on component mount.
     *
     * Dispatches a Redux action to retrieve the current attendance period request,
     * which is required for rendering and validating table data.
     *
     * Runs only once when the component is mounted.
     */
    useEffect(() => {
        dispatch(GetAttendancePeriodRequest());
    }, []);

    /**
     * Generates table header configuration.
     *
     * Dynamically builds table columns based on user role (admin or not).
     *
     * @function getThead
     * @returns {Array<JSX.Element>} Array of table header cells
     */
    const getThead = () => {
        return [
            isAdmin && <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t('supervisor')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('created')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('headcount')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('approval_status')}</CustomTh>,
        ];
    };

    /**
     * Generates table body rows based on fetched results.
     *
     * Maps each request item into a structured row with:
     * - Supervisor (if admin)
     * - Request date and time
     * - Headcount
     * - Approval status
     *
     * Handles empty states by returning an empty structure.
     *
     * @function getTbody
     * @returns {Object} Table body configuration
     * @returns {Array<JSX.Element>} returns.body - Rendered rows
     * @returns {Array<Object>} returns.data - Raw data associated with rows
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return {
            body: [],
            data: []
        };

        let body = results?.content?.map((item) => {
            return (
                <>
                    { isAdmin &&
                        <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={"first-column"}>
                            <CustomImageTable image={IconAvatarTable} containerClass={"d-flex align-items-center"} text={item?.requestedBy}/>
                        </CustomTd>
                    }
                    <CustomTd tableId={tableId}>
                        <div className={"d-flex"}>
                            <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"} />
                            {getDateFormat( item?.requestDate, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                        </div>
                        <div className={"d-flex"}>
                            <IconClockTable className={"mr-8px mb-1 icon-color-general-950"} />
                            {getTimeFormat(item?.requestDate)}
                        </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {item?.totalItems || EMPTY_VALUES.DEFAULT}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip legend={t(status[item?.approvalStatusId])}
                                    borderColor={statusBorderColor[item?.approvalStatusId]}
                                    fontColor={statusFontColor[item?.approvalStatusId]}
                                    background={statusColor[item?.approvalStatusId]}
                                    variant={'large'}
                        />
                    </CustomTd>
                </>
            );
        });

        return {
            body: body, // Return the generated table rows
            data: results?.content // Return the data associated with the rows
        };
    };

    /**
     * Generates action menu options for each table row.
     *
     * Actions vary depending on:
     * - User permissions
     * - Request status
     * - Ownership of the request
     *
     * Possible actions include:
     * - Batch approval
     * - Approve
     * - Reject
     * - Modify request
     * - View details
     *
     * @function getActionsButtons
     * @param {Object} item - Row data
     * @param {number} index - Row index
     * @returns {JSX.Element} Dropdown menu with actions
     */
    const getActionsButtons = (item, index) => {
        const showChangeRequestedBtn = (item.requestedById === getUserByIdAtEntry?.userId)
            && item.approvalStatusId === STATUS_MODEL.CHANGES_REQUESTED;
        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item.id}`} // Unique menu ID for each contractor
            >

                {(DEV || permission.includes(permissionObj?.WEB_ATTENDANCE_SUBMISSION_REQUEST_MANAGE_APPROVAL)) &&
                item.approvalStatusId !== STATUS_MODEL.CHANGES_REQUESTED ?
                    [
                        <DropDownMenuItem
                            id={'btn_art_batch_approval_' + item?.id}
                            index={index}
                            handleClose={handleClose}
                            name={t('batch_approval')}
                            click={() => handleBatchApproval(item)}
                            icon={<DetailsIconsOptions />}
                        />,
                        <DropDownMenuItem
                            id={'btn_art_approve_' + item?.id}
                            index={index}
                            handleClose={handleClose}
                            name={t('approve')}
                            click={() => {
                                setRow(item);
                                setShowApprove(true);
                            }}
                            icon={<CheckIconsOptions />}
                        />,
                        <DropDownMenuItem
                            id={'btn_art_reject_' + item?.id}
                            index={index}
                            handleClose={handleClose}
                            name={t('reject')}
                            fontColor={'font-color-error-600'}
                            iconColor={"icon-color-error-code-600"}
                            click={() => {
                                setRow(item);
                                setShowDenied(true);
                            }}
                            icon={<IconCloseChip />}
                        />,
                        showChangeRequestedBtn &&
                            <DropDownMenuItem
                                id={'btn_art_modify_request_' + item?.id}
                                index={index}
                                handleClose={handleClose}
                                name={t('modify_request')}
                                click={() => handleChangeRequest(item)}
                                icon={<EditUnderlinedIcon width={24} height={24}/>}
                            />
                    ]
                    :
                    [
                        <DropDownMenuItem
                            id={'btn_art_details_' + item?.id}
                            index={index}
                            handleClose={handleClose}
                            name={t('details')}
                            click={() => handleSubmissionDetails(
                                `${routes.submission.requestDetails}${item?.id}`,
                                item,
                                navigate,
                                ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUESTED
                            )}
                            icon={<DetailsIconsOptions />}
                        />,
                        showChangeRequestedBtn &&
                            <DropDownMenuItem
                                id={'btn_art_modify_request_' + item?.id}
                                index={index}
                                handleClose={handleClose}
                                name={t('modify_request')}
                                click={() => handleChangeRequest(item)}
                                icon={<EditUnderlinedIcon width={24} height={24}/>}
                            />
                    ]
                }
            </DropDownMenuProfile>
        );
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page, { searchElement }) => {
        const body = SEARCH_MODEL.setObject({page, size:rowsPerPage, model:option, key:searchElement })
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(
            ({payload: {data: { data, success }}}) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    }

    // Use the TableFunction SubComponents to handle table logic (checkbox selection, pagination, etc.)
    const { thead,tbody, t, pagination, rowsPerPage,
        openMenu, handleMenuToggle, handleClose } = TableFunction({
        getTbody: getTbody,
        getThead: getThead,
        selectedList,
        setSelectedList,
        data: results,
        labelRowsPerPage: 'employees_per_page',
        hasContainer: true,
        fixedPagination: false,
        setIsAllChecked,
        searchElement,

        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Navigates to batch approval screen for a selected request.
     *
     * Passes navigation state to preserve:
     * - Previous route
     * - Current tab and subview
     *
     * @function handleBatchApproval
     * @param {Object} row - Selected request row
     * @returns {void}
     */
    const handleBatchApproval = (row) => {
        navigate(`${routes.submission.requestedBatchApproval}${row?.id}`, {
            state: {
                backUrl: routes.attendance.main,
                currentTab: TABS_ATTENDANCE.SUBMISSIONS.INDEX,
                currentSubView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUESTED
            }
        });
    };

    /**
     * Navigates to change request screen for a selected request.
     *
     * Includes request ID and navigation context in route state.
     *
     * @function handleChangeRequest
     * @param {Object} row - Selected request row
     * @returns {void}
     */
    const handleChangeRequest = (row) => {
        navigate(`${routes.submission.changeRequest}`, {
            state: {
                backUrl: routes.attendance.main,
                currentTab: TABS_ATTENDANCE.SUBMISSIONS.INDEX,
                currentSubView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUESTED,
                requestId: row.id
            }
        });
    };

    return {
        t,
        dispatch,
        navigate,
        setResults,
        rowsPerPage,
        tableId,
        searchId,
        thead,
        tbody,
        pagination,
        getActionsButtons,
        genericCommentModals,
        getAttendancePeriodRequest,
        row
    };
};

export default AttendanceRequestedTableApp;
